<script setup lang="ts">
/**
 * Step 4 — Controllers (RORC). Exempt/identify flags + individual/corporate
 * controllers + supporting documents. Mirrors the controller block in HTML step 5.
 */
import { ref, watch } from 'vue';
import SgIdTypeField from '@/features/setupEntity/components/sg/shared/SgIdTypeField.vue';
import SgB04NationalityField from '@/features/setupEntity/components/sg/shared/SgB04NationalityField.vue';
import SgJurisdictionDateField from '@/features/setupEntity/components/sg/shared/SgJurisdictionDateField.vue';
import SgMobilePhoneFields from '@/features/setupEntity/components/sg/shared/SgMobilePhoneFields.vue';
import SgAddressFields from '@/features/setupEntity/components/sg/shared/SgAddressFields.vue';
import SgEfDocUpload from '../components/SgEfDocUpload.vue';
import CmBaseCodeAutocomplete from '../../shared/CmBaseCodeAutocomplete.vue';
import {
  createEmptyController,
  type EfController,
} from '../composables/incorporationEfilingModel';
import type { EfUploadedFile } from '../composables/useIncorporationEfiling';

const props = defineProps<{
  controllers: EfController[];
  rorc: { exempt: boolean; identify: boolean; exemptCategories: string[] };
  uploadedFiles: EfUploadedFile[];
  readonly?: boolean;
  /** 밸리데이션 포커스: 이 인덱스의 컨트롤러 아코디언을 강제로 연다. */
  openIndex?: number | null;
}>();

const openPanels = ref<number[]>([]);
watch(
  () => props.openIndex,
  (idx) => {
    if (typeof idx === 'number' && idx >= 0 && !openPanels.value.includes(idx)) {
      openPanels.value = [...openPanels.value, idx];
    }
  },
);

// RORC 면제 카테고리(코드 6~11) — bizfile 정식 문구. 코드 6→(a) … 11→(f) 순서.
const RORC_EXEMPT_CATEGORIES = [
  {
    value: '6',
    label:
      '(a) Entity is a public company which shares are listed for quotation on an approved exchange in Singapore',
  },
  { value: '7', label: '(b) Entity is a company that is a Singapore financial institution' },
  { value: '8', label: '(c) Entity is a company that is wholly owned by the Government' },
  {
    value: '9',
    label:
      '(d) Entity is a company that is wholly owned by a statutory body established by or under a public Act for a public purpose',
  },
  {
    value: '10',
    label:
      '(e) Entity is a company that is a wholly owned subsidiary of a company mentioned in sub-paragraph (a), (b), (c) or (d)',
  },
  {
    value: '11',
    label:
      '(f) Entity is a company which shares are listed on a securities exchange in a country or territory outside Singapore and which is subject to - i. regulatory disclosure requirements; and ii. requirements relating to adequate transparency in respect of its beneficial owners (imposed through stock exchange rules, law or other enforceable means)',
  },
];

function addController(kind: 'IND' | 'COM') {
  props.controllers.push(createEmptyController(kind));
}
function removeController(i: number) {
  props.controllers.splice(i, 1);
}
</script>

<template>
  <div class="ef-step">
    <div class="ef-step__section">
      <div class="ef-step__h">Register of Registrable Controllers (RORC)</div>
      <v-row dense>
        <v-col cols="12" sm="6">
          <v-select
            v-model="rorc.exempt"
            :items="[
              { title: 'No — register controllers', value: false },
              { title: 'Yes — exempted', value: true },
            ]"
            label="Exempted from RORC?"
            density="compact"
            variant="outlined"
            hide-details
            :readonly="readonly"
          />
        </v-col>
        <v-col v-if="!rorc.exempt" cols="12" sm="6">
          <v-select
            v-model="rorc.identify"
            :items="[
              { title: 'Yes — able to identify', value: true },
              { title: 'No — unable to identify', value: false },
            ]"
            label="Able to identify registrable controller(s)?"
            density="compact"
            variant="outlined"
            hide-details
            :readonly="readonly"
          />
        </v-col>
      </v-row>

      <div v-if="rorc.exempt" class="mt-2">
        <div class="ef-step__sub">Category of exemption</div>
        <div class="rorc-cats">
          <v-checkbox
            v-for="c in RORC_EXEMPT_CATEGORIES"
            :key="c.value"
            v-model="rorc.exemptCategories"
            :label="c.label"
            :value="c.value"
            density="compact"
            hide-details
            :disabled="readonly"
            class="rorc-cats__item"
          />
        </div>
      </div>
    </div>

    <div v-if="!rorc.exempt && rorc.identify" class="ef-step__section">
      <div class="ef-step__h">Controllers</div>
      <div v-if="!controllers.length" class="ef-step__empty">No controllers added.</div>

      <v-expansion-panels v-else v-model="openPanels" multiple variant="accordion" class="mb-3">
        <v-expansion-panel v-for="(c, i) in controllers" :key="i">
          <v-expansion-panel-title>
            <span class="rc-title">
              {{ c.kind === 'IND' ? 'Individual' : 'Corporate' }} —
              {{ c.name || c.entityName || '(controller)' }}
            </span>
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <!-- individual -->
            <template v-if="c.kind === 'IND'">
              <v-row dense>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="c.name"
                    label="Name"
                    density="compact"
                    variant="outlined"
                    hide-details
                    :readonly="readonly"
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="c.nameAlias"
                    label="Name alias"
                    density="compact"
                    variant="outlined"
                    hide-details
                    :readonly="readonly"
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <SgIdTypeField v-model="c.idType" :readonly="readonly" />
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="c.idNo"
                    label="ID No"
                    density="comfortable"
                    variant="outlined"
                    hide-details
                    :readonly="readonly"
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <SgB04NationalityField v-model="c.nationality" :readonly="readonly" />
                </v-col>
                <v-col cols="12" sm="6">
                  <SgJurisdictionDateField v-model="c.birthDate" label="Birth date" :readonly="readonly" />
                </v-col>
              </v-row>
            </template>

            <!-- corporate -->
            <template v-else>
              <v-row dense>
                <v-col cols="12" sm="6">
                  <v-select
                    v-model="c.registeredLocally"
                    :items="[
                      { title: 'Yes (local SG entity)', value: 'YES' },
                      { title: 'No (foreign entity)', value: 'NO' },
                    ]"
                    label="Registered locally?"
                    density="compact"
                    variant="outlined"
                    hide-details
                    :readonly="readonly"
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <CmBaseCodeAutocomplete
                    v-model="c.country"
                    main-code="B15"
                    label="Jurisdiction / Country"
                    :read-only="readonly"
                  />
                </v-col>
                <v-col v-if="c.registeredLocally === 'YES'" cols="12" sm="6">
                  <v-text-field
                    v-model="c.uen"
                    label="Entity UEN"
                    density="compact"
                    variant="outlined"
                    hide-details
                    :readonly="readonly"
                  />
                </v-col>
                <template v-else>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="c.fbrn"
                      label="Foreign business reg number"
                      density="compact"
                      variant="outlined"
                      hide-details
                      :readonly="readonly"
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="c.entityName"
                      label="Entity name"
                      density="compact"
                      variant="outlined"
                      hide-details
                      :readonly="readonly"
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="c.registrar"
                      label="Name of corporate entity register"
                      density="compact"
                      variant="outlined"
                      hide-details
                      :readonly="readonly"
                    />
                  </v-col>
                </template>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="c.legalForm"
                    label="Legal form"
                    density="compact"
                    variant="outlined"
                    hide-details
                    :readonly="readonly"
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="c.statute"
                    label="Statute"
                    density="compact"
                    variant="outlined"
                    hide-details
                    :readonly="readonly"
                  />
                </v-col>
              </v-row>
            </template>

            <v-row dense class="mt-1">
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="c.email"
                  label="Email"
                  density="compact"
                  variant="outlined"
                  hide-details
                  :readonly="readonly"
                />
              </v-col>
              <v-col cols="12">
                <SgMobilePhoneFields
                  v-model:country-code="c.countryCode"
                  v-model:area-code="c.areaCode"
                  v-model:mobile-no="c.contactNo"
                  v-model:area-code-not-applicable="c.noArea"
                  :readonly="readonly"
                  show-area-code-toggle
                />
              </v-col>
            </v-row>

            <div class="ef-step__sub">Address</div>
            <SgAddressFields
              :model-value="c.address"
              :readonly="readonly"
              show-level-unit-toggle
              @update:model-value="c.address = $event"
            />

            <v-select
              v-model="c.confirmationFlag"
              :items="[
                { title: 'Yes', value: true },
                { title: 'No', value: false },
              ]"
              label="Confirmation by Registrable Controller"
              density="compact"
              variant="outlined"
              hide-details
              class="mt-2"
              :readonly="readonly"
            />

            <div class="mt-2">
              <div class="ef-step__sub">Supporting documents (max 2)</div>
              <SgEfDocUpload
                :docs="c.documents"
                :max-count="2"
                :readonly="readonly"
                :uploaded-files="uploadedFiles"
                hint="Attach or replace controller supporting PDFs."
              />
            </div>

            <div v-if="!readonly" class="rc-actions">
              <v-btn
                variant="text"
                color="error"
                size="small"
                class="text-none"
                @click="removeController(i)"
              >
                Remove
              </v-btn>
            </div>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>

      <div v-if="!readonly" class="rc-add">
        <v-btn
          variant="tonal"
          color="primary"
          size="small"
          class="text-none"
          prepend-icon="mdi-plus"
          @click="addController('IND')"
        >
          Add individual controller
        </v-btn>
        <v-btn
          variant="tonal"
          color="primary"
          size="small"
          class="text-none"
          prepend-icon="mdi-plus"
          @click="addController('COM')"
        >
          Add corporate controller
        </v-btn>
      </div>
    </div>
  </div>
</template>

<style scoped>
.ef-step__section {
  margin-bottom: 18px;
}
.ef-step__h {
  font-size: 14px;
  font-weight: 700;
  color: #1a2336;
  margin-bottom: 8px;
}
.ef-step__sub {
  font-size: 12.5px;
  font-weight: 700;
  color: #42506a;
  margin: 10px 0 4px;
}
.ef-step__empty {
  font-size: 12.5px;
  color: #8a95a8;
  padding: 8px 0;
}
.rc-title {
  font-weight: 600;
  font-size: 13px;
}
.rorc-cats {
  display: flex;
  flex-direction: column;
  gap: 0;
}
.rc-mobile {
  display: flex;
  gap: 6px;
}
.rc-doc {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12.5px;
  margin-right: 12px;
  text-decoration: none;
  color: #2563eb;
}
.rc-add {
  display: flex;
  gap: 8px;
}
.rc-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
}
</style>
